import {
  listConversations,
  deleteConversation,
  type AiConversationSummary,
  type AiConversationsApiOptions,
} from '@/services/aiConversations';

/**
 * The "recent chats" list behind the browse UI in both hosts (floating panel
 * header menu + full-page sidebar). Pure list state — selecting a conversation
 * is the host's job (it calls chat.loadConversation).
 */
export function useAiConversations(options: AiConversationsApiOptions = {}) {
  const conversations = ref<AiConversationSummary[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const loaded = ref(false);

  const refresh = async(limit = 30) => {
    loading.value = true;
    error.value = null;
    try {
      conversations.value = await listConversations(limit, options);
      loaded.value = true;
    } catch (e) {
      error.value = e instanceof Error && e.message
        ? e.message
        : 'Could not load your recent chats.';
    } finally {
      loading.value = false;
    }
  };

  /** Delete server-side, then drop it from the list (optimistic on success). */
  const remove = async(id: number): Promise<boolean> => {
    error.value = null;
    try {
      await deleteConversation(id, options);
      conversations.value = conversations.value.filter((c) => c.id !== id);
      return true;
    } catch (e) {
      error.value = e instanceof Error && e.message
        ? e.message
        : 'Could not delete that conversation.';
      return false;
    }
  };

  const isEmpty = computed(() => loaded.value && !loading.value && conversations.value.length === 0);

  return {
    conversations,
    loading,
    error,
    loaded,
    isEmpty,
    refresh,
    remove,
  };
}

export type UseAiConversationsReturn = ReturnType<typeof useAiConversations>;
